import React from 'react';
import PropTypes from 'prop-types';
import { useMutation } from 'react-query';
import axios, { endPoints } from 'api/request';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { activeAnnouncementRefresh, restrictedAnnouncementCatalogs } from 'store';
import { setAnnouncementRestrictions } from 'utils/announcementRestrictions';
import FreezeBanner from 'components/FreezeBanner';

const POLL_INTERVAL = 10 * 60 * 1000; // 10 minutes

const isAnnouncementActive = (announcement) => {
    if (!announcement?.active) return false;
    const now = new Date();
    const start = new Date(announcement.startDate);
    const end = new Date(announcement.endDate);
    return now >= start && now <= end;
};

const ActiveAnnouncements = ({ isVisible }) => {
    const [announcements, setAnnouncements] = React.useState([]);
    const [dismissedAnnouncements, setDismissedAnnouncements] = React.useState(() => new Set());
    const refreshTrigger = useRecoilValue(activeAnnouncementRefresh);
    const setRestrictedCatalogs = useSetRecoilState(restrictedAnnouncementCatalogs);
    const activeApi = useMutation(async () => axios.get(endPoints?.announcements?.active));

    React.useEffect(() => {
        activeApi.mutateAsync();
        const interval = setInterval(() => {
            activeApi.mutateAsync();
        }, POLL_INTERVAL);
        return () => clearInterval(interval);
    }, []);

    // Re-fetch when triggered externally (e.g., after create/update/delete)
    React.useEffect(() => {
        if (refreshTrigger > 0) {
            setDismissedAnnouncements(new Set());
            activeApi.mutateAsync();
        }
    }, [refreshTrigger]);

    React.useEffect(() => {
        if (activeApi?.isSuccess) {
            const list = (activeApi?.data?.data || []).filter(isAnnouncementActive);
            setAnnouncements(list);

            // Extract, deduplicate, and store restricted catalogs globally
            const restrictions = setAnnouncementRestrictions(list);
            setRestrictedCatalogs(restrictions);
        }
    }, [activeApi?.isSuccess, activeApi?.data]);

    const handleClose = (announcementKey) => {
        setDismissedAnnouncements((previous) => {
            const updated = new Set(previous);
            updated.add(announcementKey);
            return updated;
        });
    };

    const visibleAnnouncements = announcements
        .map((announcement, index) => ({ announcement, key: announcement.id ?? index }))
        .filter(({ key }) => !dismissedAnnouncements.has(key));

    if (!isVisible || !visibleAnnouncements.length) return null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {visibleAnnouncements.map(({ announcement, key }) => {
                const showCloseIcon = announcement.showCloseIcon ?? true;

                return (
                    <Box key={key} sx={{ position: 'relative' }} data-testid={`active-announcement-${key}`}>
                        <FreezeBanner severity={announcement.severity} message={announcement.message} />
                        {showCloseIcon && (
                            <IconButton
                                onClick={() => handleClose(key)}
                                sx={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 8,
                                    color: 'inherit',
                                    background: 'rgba(255,255,255,0.3)',
                                    '&:hover': { background: 'rgba(255,255,255,0.5)' },
                                }}
                                size="small"
                                aria-label="Close announcement"
                                data-testid={`active-announcement-close-${key}`}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

ActiveAnnouncements.propTypes = {
    isVisible: PropTypes.bool,
};

ActiveAnnouncements.defaultProps = {
    isVisible: true,
};

export default ActiveAnnouncements;
